'use client';

import { ClickAwayListener } from '@mui/material';
import type { SxProps, BadgeProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Box,
  type BoxProps,
  ConditionalWrapper,
  Badge as MuiBadge,
  Link,
  Typography,
} from '@packages/shared';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { localizeUrl } from '@packages/shared/src/utils/localizeUrl/localizeUrl';
import { useIsHydrated } from '@packages/shared/src/hooks/useIsHydrated/useIsHydrated';
import type { KeyboardEvent, MouseEvent, MouseEventHandler, ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import type { Language } from '@packages/config';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { SlotPopover } from './SlotPopover';
import type { SlotPriority } from './types';

// Add support for the sx prop for consistency with the other branches.
const Anchor = styled('a')({
  color: 'inherit',
  textDecoration: 'none',
});

export type SlotProps = {
  /** Use router link or default <a> tags
   * @default true
   */
  useRouterLink?: boolean;
  /** add a language to the router link for route based i18n
   * @default undefined
   */
  language?: Language;
  /** Slot link */
  href?: string;
  /** Aria label for Slot link */
  linkAriaLabel?: string;
  /** Main icon component */
  icon: ReactElement<any>;
  /** Slot title */
  title?: string;
  /** Set badgeCount to show primary badge in the top right corner on the icon */
  badgeCount?: number;
  /** Slot popover content */
  children?: ReactNode | ((leftPosition: number, closePopover: () => void) => ReactNode);
  priority: SlotPriority;
  /** OnClick handler */
  onClick?: MouseEventHandler<HTMLAnchorElement>;
} & Pick<BoxProps, 'sx'>;

export type PopoverHandlerEvent = MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>;

const activeStyle: SxProps = {
  '&:after': {
    content: "''",
    position: 'absolute',
    inset: 'auto 0 0 0',
    height: 2,
    bgcolor: 'primary.main',
    pointerEvents: 'none',
  },
};

const Badge = styled(MuiBadge)<BadgeProps>({
  '& .MuiBadge-badge': {
    right: 2,
    top: 6,
  },
});

/** Base component for toolbar slots */
export const Slot = ({
  href,
  linkAriaLabel,
  icon,
  badgeCount,
  title,
  useRouterLink = true,
  language,
  children,
  priority,
  onClick,
  sx,
}: SlotProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { isDesktop } = useDeviceType();
  const config = useConfig();

  const handleOpenPopover = (event: PopoverHandlerEvent) => setAnchorEl(event.currentTarget);

  const handleClosePopover = () => setAnchorEl(null);

  const handleTogglePopover = (event: PopoverHandlerEvent) => {
    anchorEl === null ? handleOpenPopover(event) : handleClosePopover();
  };

  const localizedHref =
    href && language && !href.startsWith('http') ? localizeUrl(href, language, config) : href;
  const onSlotItemClick = (event: PopoverHandlerEvent) => {
    if (!anchorEl && children) {
      event.preventDefault();
      event.stopPropagation();
      handleTogglePopover(event);
    } else {
      handleClosePopover();
    }
  };

  const { isHydrated } = useIsHydrated();

  return (
    <ClickAwayListener onClickAway={handleClosePopover}>
      <Box
        tabIndex={!href ? 0 : undefined}
        onMouseEnter={isDesktop ? handleOpenPopover : undefined}
        onMouseLeave={isDesktop ? handleClosePopover : undefined}
        onClick={onSlotItemClick}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter')
            !(e.target instanceof HTMLAnchorElement) && !href && onSlotItemClick(e);
        }}
        data-testid="header-slot-wrapper"
        sx={sx}
        className={`slot-mobile-priority-${priority}`}
      >
        {/* conditionaly wrap slot in a link */}
        <ConditionalWrapper
          condition={!!href}
          wrapper={(c) =>
            !useRouterLink ? (
              <Anchor href={localizedHref} onClick={onClick} aria-label={linkAriaLabel ?? title}>
                {c}
              </Anchor>
            ) : (
              <Link
                noLinkStyle
                lang={language}
                href={localizedHref!}
                locale={language}
                onClick={onClick}
                aria-label={linkAriaLabel ?? title}
              >
                {c}
              </Link>
            )
          }
        >
          {/* content wrapper (icon and title) */}
          <Box
            onClick={!isDesktop && !href ? handleTogglePopover : undefined}
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100%',
              cursor: 'pointer',
              '> svg': {
                color: anchorEl ? 'primary.main' : 'auto',
                width: 32,
                height: 32,
              },
              py: 0.5,
              ...(!!anchorEl && isDesktop && activeStyle),
            }}
            {...(isDesktop || !children
              ? {}
              : { title, 'aria-label': linkAriaLabel, role: 'button' })}
            aria-disabled={!isDesktop && !href && !isHydrated}
            data-testid="header-slot-content"
          >
            {/* conditionaly show primary badge at top right of the slot icon */}
            <ConditionalWrapper
              condition={!!badgeCount}
              wrapper={(ch) => (
                <Badge badgeContent={badgeCount} data-testid="header-slot-badge">
                  {ch}
                </Badge>
              )}
            >
              {icon}
            </ConditionalWrapper>
            {/* optionally show title under slot icon (mobile slots do not set title) */}
            {title && isDesktop && (
              <Typography
                variant="body3"
                textAlign="center"
                data-testid="header-slot-title"
                className="hyphens-auto"
              >
                {title}
              </Typography>
            )}
          </Box>
        </ConditionalWrapper>
        {children && anchorEl && (
          <SlotPopover anchorEl={anchorEl} onClose={handleClosePopover}>
            {typeof children === 'function'
              ? children(anchorEl.getBoundingClientRect().left, handleClosePopover)
              : children}
          </SlotPopover>
        )}
      </Box>
    </ClickAwayListener>
  );
};
